import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import GridPeriod from "../Grid/GridPeriod.jsx";
import MonthPicker from "../MonthPicker/MonthPicker.jsx";
import PeriodEditor from "../PeriodEditor/PeriodEditor.jsx";
import {
    MATERIAL_UI_PROPS,
    monthNames,
    STATE_DATE_FORMAT,
} from "../../lib/data";
import { turnNumbertoObjkey } from "../../lib/helperFuncs.jsx";

import EditIcon from "@material-ui/icons/Edit";
import {
    Box,
    LinearProgress,
    Button,
    Paper,
    Tab,
    Tabs,
    Typography,
    Dialog,
    DialogTitle,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import axios from "axios";
import modalOpenStyle from "./modalOpenStyle.module.css";

const GridPeriodResidence = (props) => {
    const web = true;

    const [month, setMonth] = useState(moment());
    const date_start = month.format("YYYY-MM-01");
    const daysInMonth = month.daysInMonth();
    const date_end = month.format(`YYYY-MM-${daysInMonth}`);
    const [cells, setCells] = useState(
        createBooleanMatrix(props.auxData.length + 1, month.daysInMonth() + 1),
    );
    const [monthFormVisible, setMonthFormVisible] = useState(false);
    const [editFormVisible, setEditFormVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedRates, setSelectedRates] = useState([]);
    const activeRates = props.rates.filter((rate) => rate.active);
    const [pickedRate, setPickedRate] = useState(activeRates[0]);
    const [update, setUpdate] = useState(1);
    const [openModalRates, setOpenModalRates] = useState(false);
    const [selectedShowRates, setSelectedShowRates] = useState([]);

    const [gridValues, setGridValues] = useState(
        createGridValues(
            month,
            props.auxData,
            props.items,
            props.objectTemplate,
            props.errorFields,
            pickedRate,
        ),
    );

    useEffect(() => {
        const rate_id = pickedRate.id ?? null;
        props.initAction({ date_start, date_end, web, rate_id });
        setCells(
            createBooleanMatrix(
                props.auxData.length + 1,
                month.daysInMonth() + 1,
            ),
        );
    }, [month, update, pickedRate]);

    useEffect(() => {
        setGridValues(
            createGridValues(
                month,
                props.auxData,
                props.items,
                props.objectTemplate,
                props.errorFields,
                pickedRate,
            ),
        );
    }, [props.items, pickedRate]);

    useEffect(() => {
        if (!props.errorFields) return undefined;
        setGridValues(
            updateGridWithResults(gridValues, props.errorFields.items),
        );
    }, [props.errorFields]);

    const actualRates = activeRates && returnActualRatesArr(activeRates, month);
    let pickedRateIdx =
        actualRates &&
        actualRates
            .filter((rate) => selectedShowRates.includes(rate.id))
            .indexOf(pickedRate);

    useEffect(() => {
        const storedSelectedRates = localStorage.getItem(
            `selectedPeriodRates${props.hotelId}`,
        );

        if (!storedSelectedRates) {
            const allIds = actualRates.map((rate) => rate.id);
            localStorage.setItem(
                `selectedPeriodRates${props.hotelId}`,
                JSON.stringify(allIds),
            );
            setSelectedShowRates(allIds);
        } else {
            setSelectedShowRates(JSON.parse(storedSelectedRates));
        }
    }, [openModalRates]);

    useEffect(() => {
        if (
            selectedShowRates.length > 0 &&
            !selectedShowRates.includes(pickedRate.id)
        ) {
            setPickedRate(
                activeRates.filter((rate) =>
                    selectedShowRates.includes(rate.id),
                )[0],
            );
        }
    }, [selectedShowRates]);

    const handleMonthForm = (monthYear) => {
        let pickedMonthIndex = monthYear === -1 ? 11 : monthYear;

        if (monthYear && isNaN(monthYear)) {
            const monthName = monthYear.split(" ")[0];
            pickedMonthIndex = monthNames.indexOf(monthName);
        }

        const curMonth = moment();
        setMonthFormVisible(false);
        if (pickedMonthIndex > -1) {
            const pickedMonth = moment().month(pickedMonthIndex);
            if (curMonth > pickedMonth) {
                pickedMonth.add(1, "year");
                setMonth(pickedMonth);
            } else if (month.isSame(pickedMonth, "month")) {
                setMonthFormVisible(false);
            } else {
                setMonth(pickedMonth);
            }
        }
    };

    const updateHandler = (min_los, max_los) => {
        setEditFormVisible(false);
        const updatedGrid = { ...gridValues };
        const itemsToUpdate = [];
        let isUpdated = false;
        cells.forEach((row, rowIndex) => {
            if (rowIndex === 0) return;
            row.forEach((col, colIndex) => {
                if (colIndex === 0) return;
                if (col) {
                    isUpdated = true;
                    const [gridRow, gridCol] = getGridIndexes(
                        rowIndex - 1,
                        colIndex,
                        updatedGrid,
                    );

                    const updatedItem = {
                        ...updatedGrid[gridRow][gridCol],
                        min_los,
                        max_los,
                        rate_id: pickedRate.id,
                    };

                    updatedGrid[gridRow][gridCol] = { ...updatedItem };
                    itemsToUpdate.push({ ...updatedItem });
                }
            });
        });
        if (isUpdated) {
            setGridValues({ ...updatedGrid });
            setCells(
                createBooleanMatrix(
                    props.auxData.length + 1,
                    month.daysInMonth() + 1,
                ),
            );
            itemsToUpdate.push(selectedRates);
            props.submitAction(itemsToUpdate);
        }
    };

    const periodEditHandler = (cells) => {
        setCells(cells);
        setEditFormVisible(true);
    };

    const closeEditForm = () => {
        setEditFormVisible(false);
        setCells(
            createBooleanMatrix(
                props.auxData.length + 1,
                month.daysInMonth() + 1,
            ),
        );
    };

    const ratesNames =
        actualRates &&
        actualRates.length > 0 &&
        actualRates
            .filter((rate) => selectedShowRates.includes(rate.id))
            .map((rate) => {
                return (
                    <Tab
                        title={rate.code !== rate.name ? rate.name : ""}
                        label={rate.code || rate.name}
                        key={`${rate.id}/${rate.code}`}
                        onClick={() => {
                            setPickedRate(rate);
                        }}
                        onDoubleClick={() => {
                            setOpenModalRates(true);
                        }}
                    />
                );
            });

    const rates = !ratesNames ? (
        <Box p={2}>
            <Typography>Нет доступных тарифов</Typography>
        </Box>
    ) : (
        <Tabs
            value={pickedRateIdx === -1 ? 0 : pickedRateIdx}
            indicatorColor={MATERIAL_UI_PROPS.colors.primary}
            textColor={MATERIAL_UI_PROPS.colors.primary}
            variant="fullWidth"
            scrollButtons="true"
        >
            {ratesNames}
        </Tabs>
    );

    const handleRateSelection = (rateId) => {
        const isRateSelected = selectedShowRates.includes(rateId);
        let updatedSelectedRates;
        if (isRateSelected) {
            updatedSelectedRates = selectedShowRates.filter(
                (id) => id !== rateId,
            );
        } else {
            updatedSelectedRates = [...selectedShowRates, rateId];
        }
        setSelectedShowRates(updatedSelectedRates);
        localStorage.setItem(
            `selectedPeriodRates${props.hotelId}`,
            JSON.stringify(updatedSelectedRates),
        );
    };

    return (
        <>
            {props?.loading ||
                (loading && (
                    <Box mt={4}>
                        <LinearProgress mt={3} />
                    </Box>
                ))}
            {monthFormVisible && (
                <MonthPicker
                    onClose={handleMonthForm}
                    open={monthFormVisible}
                    thirteenMonthNotVisible
                />
            )}
            <Paper style={{ marginBottom: "30px" }} elevation={3}>
                {rates}
            </Paper>
            <GridPeriod
                key={update}
                auxData={props.auxData}
                gridValues={gridValues}
                onSelect={periodEditHandler}
                onMonthSelect={() => setMonthFormVisible(true)}
                onMonthChange={handleMonthForm}
                month={month}
                cells={cells}
                openLeftMenu={props.openLeftMenu}
            />
            {editFormVisible && (
                <PeriodEditor
                    isVisible={editFormVisible}
                    onClose={closeEditForm}
                    onSubmit={updateHandler}
                    rates={activeRates}
                    selectedRates={selectedRates}
                    setSelectedRates={setSelectedRates}
                    rateId={pickedRate.id}
                />
            )}
            <Dialog
                open={openModalRates}
                onClose={() => setOpenModalRates(false)}
                maxWidth="xl"
            >
                <DialogTitle>Тарифы для отображения</DialogTitle>
                {openModalRates && (
                    <div style={{ padding: "10px" }}>
                        {actualRates.map((rate) => (
                            <div key={rate.id}>
                                <input
                                    className={modalOpenStyle.inputModalRates}
                                    type="checkbox"
                                    checked={selectedShowRates.includes(
                                        rate.id,
                                    )}
                                    onChange={() =>
                                        handleRateSelection(rate.id)
                                    }
                                    id={rate.name + rate.id}
                                />
                                <label
                                    style={{ marginLeft: "5px" }}
                                    htmlFor={rate.name + rate.id}
                                >
                                    {rate.name}
                                </label>
                            </div>
                        ))}
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default GridPeriodResidence;

/**
 * Return array of rates that are active in given dates
 *
 * @param {Array} rates
 * @param {moment} curMonth
 */

const returnActualRatesArr = (rates, curMonth) => {
    const curDate = moment().format(STATE_DATE_FORMAT);
    return rates.filter((rate) => {
        const rateMomentEndDate = moment(rate.date_end, STATE_DATE_FORMAT);
        if (
            rateMomentEndDate >= curMonth ||
            rateMomentEndDate.isBetween(
                curMonth.startOf("month").format(STATE_DATE_FORMAT),
                curMonth.endOf("month").format(STATE_DATE_FORMAT),
                undefined,
                "[]",
            )
        )
            return true;
    });
};

const createBooleanMatrix = (rows, cols) => {
    const matrix = [];
    for (let i = 0; i < rows; i++) {
        let row = [];
        for (let j = 0; j < cols; j++) {
            row.push(false);
        }
        matrix.push(row);
    }
    return matrix;
};

const createGridValues = (
    curMoment,
    roomTypes,
    items,
    objectTemplate,
    errorFields = null,
    rate,
) => {
    const grid = {};
    const daysInMonth = curMoment.daysInMonth();
    roomTypes.forEach((roomType) => {
        const idStr = turnNumbertoObjkey(roomType.id);
        grid[idStr] = {};
        for (let i = 1; i <= daysInMonth; i++) {
            const day_DD = `${i}`.padStart(2, "0");

            grid[idStr][i] = {
                ...objectTemplate,
                date: curMoment.format(`YYYY-MM-${day_DD}`),
                room_type_id: parseInt(roomType.id),
            };
        }
    });

    items.forEach((item) => {
        const { room_type_id } = { ...item };
        const { rate_id } = { ...item };
        const date = +moment(item.date).format("D");
        grid[turnNumbertoObjkey(room_type_id)][date] = {
            ...item,
        };
    });

    return grid;
};

const getGridIndexes = (rowIndex, colIndex, gridValues) => {
    const gridRow = Object.keys(gridValues)[rowIndex];
    return [gridRow, colIndex];
};

const updateGridWithResults = (gridValues, results) => {
    const updatedGrid = { ...gridValues };
    Object.entries(results).forEach((entry) => {
        const [roomTypeId, roomTypeResults] = [...entry];
        const roomTypeIdkey = turnNumbertoObjkey(roomTypeId);
        Object.keys(updatedGrid[roomTypeIdkey]).forEach((key) => {
            const gridCell = updatedGrid[roomTypeIdkey][key];
            const date = gridCell.date;
            if (roomTypeResults[date]) {
                gridCell.result = { ...roomTypeResults[date] };
            }
        });
    });

    return updatedGrid;
};
